import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { Account } from "models/Account";
import { Asset } from "models/Asset";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { assetTypeIcon } from "./assetPresentation";

interface AssetHierarchyTreeProps {
    asset: Asset;
    assets: Asset[];
    accounts: Account[];
}

type HierarchyRoot =
    | {kind: "account"; id: string; name: string; assets: Asset[]}
    | {kind: "asset"; asset: Asset};

interface HierarchyModel {
    assetsByID: Map<string, Asset>;
    defaultExpanded: Set<string>;
    roots: HierarchyRoot[];
}

const accountNodeID = (accountID: string) => `account:${accountID}`;
const byName = (left: Asset, right: Asset) => left.name.localeCompare(right.name);

const buildHierarchy = (current: Asset, assets: Asset[], accounts: Account[]): HierarchyModel => {
    const assetsByID = new Map(assets.map(asset => [asset.id, asset]));
    assetsByID.set(current.id, current);
    const accountNames = new Map(accounts.map(account => [account.id, account.legal_name]));
    const pathIDs = new Set<string>([current.id]);
    const topAssets = new Map<string, Asset>();

    const findTopAssets = (candidate: Asset, visited: Set<string>) => {
        if (visited.has(candidate.id)) return;
        const branch = new Set(visited).add(candidate.id);
        pathIDs.add(candidate.id);
        const parents = (candidate.parent_ids ?? [])
            .map(parentID => assetsByID.get(parentID))
            .filter((parent): parent is Asset => Boolean(parent));
        if (!parents.length) {
            topAssets.set(candidate.id, candidate);
            return;
        }
        parents.forEach(parent => findTopAssets(parent, branch));
    };

    findTopAssets(current, new Set());
    if (!topAssets.size) topAssets.set(current.id, current);

    const roots: HierarchyRoot[] = [];
    const addedAccounts = new Set<string>();
    topAssets.forEach(topAsset => {
        if (!topAsset.customer_id) {
            roots.push({kind: "asset", asset: topAsset});
            return;
        }
        if (addedAccounts.has(topAsset.customer_id)) return;
        addedAccounts.add(topAsset.customer_id);
        const accountAssets = Array.from(assetsByID.values())
            .filter(candidate => candidate.customer_id === topAsset.customer_id)
            .filter(candidate => !(candidate.parent_ids ?? []).some(parentID => assetsByID.has(parentID)))
            .sort(byName);
        if (!accountAssets.some(candidate => candidate.id === topAsset.id)) accountAssets.push(topAsset);
        roots.push({
            kind: "account",
            id: topAsset.customer_id,
            name: accountNames.get(topAsset.customer_id) ?? topAsset.customer_id,
            assets: accountAssets.sort(byName),
        });
    });

    roots.sort((left, right) => {
        const leftName = left.kind === "account" ? left.name : left.asset.name;
        const rightName = right.kind === "account" ? right.name : right.asset.name;
        return leftName.localeCompare(rightName);
    });

    return {
        assetsByID,
        roots,
        defaultExpanded: new Set([
            ...Array.from(pathIDs),
            ...roots.filter((root): root is Extract<HierarchyRoot, {kind: "account"}> => root.kind === "account").map(root => accountNodeID(root.id)),
        ]),
    };
};

const AssetHierarchyTree: React.FC<AssetHierarchyTreeProps> = ({asset, assets, accounts}) => {
    const {t} = useOfficeTranslation();
    const model = useMemo(() => buildHierarchy(asset, assets, accounts), [accounts, asset, assets]);
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set(model.defaultExpanded));

    const toggle = (nodeID: string) => setExpanded(current => {
        const next = new Set(current);
        if (next.has(nodeID)) next.delete(nodeID);
        else next.add(nodeID);
        return next;
    });

    const renderAsset = (candidate: Asset, visited: Set<string>): React.ReactNode => {
        const branch = new Set(visited).add(candidate.id);
        const children = (candidate.child_ids ?? [])
            .map(childID => model.assetsByID.get(childID))
            .filter((child): child is Asset => child !== undefined)
            .filter(child => !branch.has(child.id))
            .sort(byName);
        const isExpanded = expanded.has(candidate.id);
        const isCurrent = candidate.id === asset.id;
        return <li role="treeitem" aria-label={candidate.name} aria-selected={isCurrent} aria-expanded={children.length ? isExpanded : undefined} key={candidate.id}>
            <div className={`asset-tree-row${isCurrent ? " is-current" : ""}`}>
                {children.length
                    ? <button type="button" className="asset-tree-toggle" aria-label={t(isExpanded ? "assets.detail.collapseBranch" : "assets.detail.expandBranch", {name: candidate.name})} onClick={() => toggle(candidate.id)}><i className={`mdi ${isExpanded ? "mdi-chevron-down" : "mdi-chevron-right"}`} /></button>
                    : <span className="asset-tree-toggle-spacer" aria-hidden="true" />}
                <i className={`mdi ${assetTypeIcon(candidate.type)} asset-tree-icon`} aria-hidden="true" />
                <Link className="asset-tree-link" aria-current={isCurrent ? "page" : undefined} to={`/assets/${candidate.id}`}>
                    <span>{candidate.name}</span>
                    <small>{candidate.type.replaceAll("_", " ")}</small>
                </Link>
            </div>
            {children.length > 0 && isExpanded && <ul role="group">{children.map(child => renderAsset(child, branch))}</ul>}
        </li>;
    };

    return <ul className="asset-tree" role="tree" aria-label={t("assets.detail.hierarchy")}>
        {model.roots.map(root => {
            if (root.kind === "asset") return renderAsset(root.asset, new Set());
            const nodeID = accountNodeID(root.id);
            const isExpanded = expanded.has(nodeID);
            return <li role="treeitem" aria-label={root.name} aria-selected={false} aria-expanded={isExpanded} key={nodeID}>
                <div className="asset-tree-row asset-tree-account">
                    <button type="button" className="asset-tree-toggle" aria-label={t(isExpanded ? "assets.detail.collapseBranch" : "assets.detail.expandBranch", {name: root.name})} onClick={() => toggle(nodeID)}><i className={`mdi ${isExpanded ? "mdi-chevron-down" : "mdi-chevron-right"}`} /></button>
                    <i className="mdi mdi-domain asset-tree-icon" aria-hidden="true" />
                    <Link className="asset-tree-link" to={`/accounts/${root.id}`}><span>{root.name}</span><small>{t("assets.detail.accountParent")}</small></Link>
                </div>
                {isExpanded && <ul role="group">{root.assets.map(rootAsset => renderAsset(rootAsset, new Set()))}</ul>}
            </li>;
        })}
    </ul>;
};

export default AssetHierarchyTree;
